'use client';

/**
 * Onboarding's configuration work, brought into the builder.
 *
 * WHAT THIS SCREEN LEARNED
 *
 * The first version offered "Bring into the builder" and copied all 37 tasks of
 * a template into a workflow — so kickoff calls, the master services agreement
 * and the go-live announcement arrived as workflow steps. They are project
 * management of the onboarding engagement: they say nothing about how the
 * organization works and never happen again after go-live.
 *
 * So this screen now says, before anything is pressed, exactly what will and
 * will not cross over. The split is shown whether or not a Setup workflow
 * exists, because the number somebody needs is "where did the other 14 go", and
 * a screen that only reports what it took invites exactly that question.
 *
 * WHY "BUILD SETUP WORKFLOW" AND NOT "ADOPT"
 *
 * A Setup workflow is a different and smaller thing than the onboarding plan.
 * Calling the button "adopt" implied the plan moved, which is what led people —
 * me included — to expect the whole plan in the builder. The plan stays where
 * it is; this derives one workflow from the half of it that describes the
 * product.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  Hammer,
  Loader2,
  RefreshCw,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { workflowsAPI, OnboardingTemplateSummary, SetupWorkflowBuildResult } from '@/services/api/workflows';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface OnboardingTemplatesPanelProps {
  /** Opens the step builder on the Setup workflow. A build that leaves you
   *  looking at the same list gives no sign it did anything. */
  onOpenTemplate: (templateId: number, templateName: string) => void;
}

export const OnboardingTemplatesPanel: React.FC<OnboardingTemplatesPanelProps> = ({ onOpenTemplate }) => {
  const { t } = useAppTranslation();
  const [templates, setTemplates] = useState<OnboardingTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  /** What the last build actually did, keyed by template. Shown in place rather
   *  than as a toast: "23 steps added, 14 engagement tasks stayed in the plan"
   *  is the sentence that stops somebody going looking for the other 14, and a
   *  toast is gone before they think to ask. */
  const [lastBuild, setLastBuild] = useState<Record<number, SetupWorkflowBuildResult>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTemplates(await workflowsAPI.listOnboardingTemplates());
    } catch {
      setError(
        t(
          'workflows.onboarding.loadFailed',
          'The onboarding templates could not be loaded. Nothing has changed; try again.'
        )
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleBuild = async (template: OnboardingTemplateSummary) => {
    setBusyId(template.id);
    setError(null);
    try {
      const result = await workflowsAPI.buildSetupWorkflow(template.id);
      setLastBuild((prev) => ({ ...prev, [template.id]: result }));
      await load();
      // Only jump into the builder on a FIRST build. On a top-up the useful
      // information is the summary line that just appeared here, and navigating
      // away from it hides the one thing the person needs to read.
      if (result.created) {
        onOpenTemplate(result.workflow_template_id, `Setup — ${template.name}`);
      }
    } catch {
      setError(
        t(
          'workflows.onboarding.buildFailed',
          'The setup workflow could not be built. Nothing was copied.'
        )
      );
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error && templates.length === 0) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (templates.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
        {t('workflows.onboarding.none', 'There are no onboarding plans available to this organization yet.')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('workflows.onboarding.heading', 'Onboarding plans')}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
          {t(
            'workflows.onboarding.intro',
            'An onboarding plan is two things: setting the product up, and running the project. Only the setup half belongs in a workflow — building one brings those tasks across as steps and leaves the kickoff calls, agreements and go-live tasks in the plan where they belong.'
          )}
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        {templates.map((template) => {
          const busy = busyId === template.id;
          const crossesOver = template.configuration_tasks + template.process_tasks;
          const built = lastBuild[template.id];

          return (
            <div
              key={template.id}
              className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{template.name}</span>
                    {template.is_platform_template ? (
                      <Badge variant="outline" size="sm">
                        <Building2 className="mr-1 h-3 w-3" />
                        {t('workflows.onboarding.platform', 'WeaveIn standard')}
                      </Badge>
                    ) : null}
                    {template.has_setup_workflow ? (
                      <Badge variant="secondary" size="sm">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        {t('workflows.onboarding.built', 'Setup workflow built')}
                      </Badge>
                    ) : null}
                  </div>

                  {template.description ? (
                    <p className="mt-1 truncate text-sm text-gray-600 dark:text-gray-400">
                      {template.description}
                    </p>
                  ) : null}

                  {/* Both halves, always. The question this answers is "where
                      did the rest go", and it is better answered before it is
                      asked. */}
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="text-gray-700 dark:text-gray-300">
                      <strong>{crossesOver}</strong>{' '}
                      {t('workflows.onboarding.crossOver', 'setup tasks become steps')}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      <strong>{template.engagement_tasks}</strong>{' '}
                      {t('workflows.onboarding.stayInPlan', 'project tasks stay in the plan')}
                    </span>
                    <span className="text-gray-400 dark:text-gray-500">
                      {t('workflows.onboarding.ofTotal', '{{total}} in total', {
                        total: template.total_tasks,
                      })}
                    </span>
                  </div>

                  {template.configuration_without_capability > 0 && (
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {t(
                        'workflows.onboarding.withoutCapability',
                        '{{count}} setup tasks do not say which part of the product they configure',
                        { count: template.configuration_without_capability }
                      )}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {template.has_setup_workflow && template.setup_workflow_template_id ? (
                    <>
                      <Button
                        variant="primary"
                        disabled={busy}
                        icon={<ArrowRight className="h-4 w-4" />}
                        onClick={() =>
                          onOpenTemplate(
                            template.setup_workflow_template_id as number,
                            template.setup_workflow_name || template.name
                          )
                        }
                      >
                        {t('workflows.onboarding.open', 'Open')}
                      </Button>
                      <Button
                        variant="outline"
                        disabled={busy}
                        icon={busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                        onClick={() => void handleBuild(template)}
                        title={t(
                          'workflows.onboarding.topUpHelp',
                          'Adds setup tasks added since this was built. Nothing already here is changed or removed.'
                        )}
                      >
                        {t('workflows.onboarding.topUp', 'Check for new tasks')}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="primary"
                      disabled={busy || crossesOver === 0}
                      icon={busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Hammer className="h-4 w-4" />}
                      onClick={() => void handleBuild(template)}
                      title={
                        crossesOver === 0
                          ? t(
                              'workflows.onboarding.nothingToBuild',
                              'Every task in this plan is project work, so there is nothing to build a workflow from.'
                            )
                          : undefined
                      }
                    >
                      {t('workflows.onboarding.build', 'Build setup workflow')}
                    </Button>
                  )}
                </div>
              </div>

              {built && (
                <p className="mt-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  {built.added > 0
                    ? t(
                        'workflows.onboarding.buildAdded',
                        '{{added}} steps added. {{excluded}} project tasks stayed in the onboarding plan.',
                        { added: built.added, excluded: built.excluded }
                      )
                    : t(
                        'workflows.onboarding.buildNothingNew',
                        'Already up to date — no setup tasks have been added since this was built.'
                      )}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OnboardingTemplatesPanel;
